import { Ref, unref } from 'vue'
import { v4 } from 'uuid'

export type TreeItem = {
  id: string
  parentId?: string
}
export type TreeItemNode<I extends TreeItem> = {
  id: string
  item: I
  children: TreeItemNode<I>[]
}

const sortNodes = <N extends { children: unknown[] }>(a: N, b: N) => {
  const aHasChildren = a.children.length > 0
  const bHasChildren = b.children.length > 0
  if (aHasChildren && !bHasChildren) {
    return -1
  }
  if (bHasChildren && !aHasChildren) {
    return 1
  }
  return 0
}
const createNode = <I extends TreeItem>(items: I[], item: I): TreeItemNode<I> => {
  const children = items
    .filter((i) => i.parentId == item.id)
    .map((i) => createNode(items, i))
    .toSorted(sortNodes)

  return {
    id: v4(),
    item,
    children,
  }
}
const rootNodes = <I extends TreeItem, N extends TreeItemNode<I>>(
  items: Ref<I[]> | I[],
): TreeItemNode<I>[] => {
  const rawItems = [...unref(items)]
  return rawItems
    .filter((i) => !i.parentId)
    .map((i) => createNode(rawItems, i))
    .toSorted(sortNodes)
}

const recursiveItemIds = <I extends TreeItem, N extends TreeItemNode<I>>(
  node: N,
  itemIds: string[] = [],
) => {
  itemIds.push(node.item.id)
  node.children.forEach((c) => recursiveItemIds(c, itemIds))
  return itemIds
}

const traverseNodeForItemId = <I extends TreeItem, N extends TreeItemNode<I>>(
  acc: string[],
  node: N,
  itemId: string,
) => {
  const isTargetNode = node.item.id == itemId
  const childHasTargetNode = node.children.some((c) => traverseNodeForItemId(acc, c, itemId))
  if (isTargetNode || childHasTargetNode) {
    acc.push(node.item.id)
    return true
  }

  return false
}
const itemIdsPathTo = <I extends TreeItem, N extends TreeItemNode<I>>(
  nodes: N[],
  itemId: string,
) => {
  const accumulator: string[] = []
  nodes.forEach((n) => traverseNodeForItemId(accumulator, n, itemId))
  return accumulator
}

type IdMap<P> = {
  [key: string]: P
}
const mapItemIdsTo = <I extends TreeItem, N extends TreeItemNode<I>, P>(
  nodes: N[],
  extractor: (item: N) => P,
  accumulator: IdMap<P> = {}
) => {
  nodes.forEach(n => {
    accumulator[n.item.id] = extractor(n)
    n.children.forEach((c) => mapItemIdsTo([c], extractor, accumulator))
  })
  return accumulator
}

export default function useTree() {
  return {
    treeRootNodes: rootNodes,
    treeRecursiveItemIds: recursiveItemIds,
    treeItemIdsPathTo: itemIdsPathTo,
    treeMapItemIdsTo: mapItemIdsTo,
  }
}
